import React from 'react'
import { styled, useTheme } from '@mui/material/styles'
import { grey } from '@mui/material/colors'
import {
  Stack,
  Button,
  SwipeableDrawer,
  CircularProgress,
  Popover,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import TimePicker from '../TimePicker/TimePicker'

import './SwipeableTimePicker.sass'

const Root = styled('div')(({ theme }) => ({
  height: '100%',
  backgroundColor: theme.palette.mode === 'light' ? grey[100] : theme.palette.background.default,
}))

function SwipeableTimePicker(props) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('md'))
  const defaultFunction = () => { }

  const {
    type,
    message = '',
    open = false,
    time = new Date(),
    exitTime = new Date(),
    anchorEl = window.document.body,
    renderMessage,
    showAllTime = true,
    allTime = true,
    loading = false,
    setOpen = defaultFunction,
    setTime = defaultFunction,
    onContinue = defaultFunction,
    setAllTime = defaultFunction,
    setExitTime = defaultFunction,
    onClose = props.setOpen ? () => props.setOpen(false) : defaultFunction,
  } = props

  const id = open ? 'simple-popover' : undefined

  const getDisabled = () => loading || props.isDisabled

  const getSwipeableTimePicker = timePicker => (
    <Root>
      <SwipeableDrawer
        className={`swipeable-timepicker ${type || ''}`}
        anchor='bottom'
        open={open}
        onClose={onClose}
        onOpen={() => setOpen(true)}
        disableSwipeToOpen={false}
      >
        {timePicker}
      </SwipeableDrawer>
    </Root>
  )

  const getPopoverTimePicker = timePicker => (
    <Popover
      className='popover'
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
    >
      {timePicker}
    </Popover>
  )

  const getRangeTimePicker = () => (
    <div className='RangeTimePicker'>
      {showAllTime && (
        <FormControlLabel
          control={(
            <Checkbox
              className='all-time'
              checked={allTime}
              onChange={e => setAllTime(e.target.checked)}
              size='small'
            />
          )}
          label={t('all day')}
        />
      )}
      <FormControlLabel
        labelPlacement='start'
        onClick={e => e.preventDefault()}
        control={(
          <TimePicker
            disabled={allTime}
            setTime={setTime}
            time={time}
          />
        )}
        label={`${t('from')}:`}
      />
      <FormControlLabel
        labelPlacement='start'
        onClick={e => e.preventDefault()}
        control={(
          <TimePicker
            disabled={allTime}
            setTime={setExitTime}
            time={exitTime}
          />
        )}
        label={`${t('to')}:`}
      />
    </div>
  )

  const getBasicTimePicker = () => (
    <TimePicker
      setTime={setTime}
      time={time}
    />
  )

  const content = (
    <div className='SwipeableTimePicker'>
      {type === 'range' ? getRangeTimePicker() : getBasicTimePicker()}
      <div className='calendar-footer'>
        {renderMessage ? renderMessage() : <p className='message'>{message}</p>}
        <Stack spacing={2} direction='row' justifyContent='center'>
          <Button
            className='footer-bottom'
            fullWidth
            variant='outlined'
            disabled={getDisabled()}
            onClick={() => (onContinue ? onContinue() : setOpen(false))}
          >
            {loading ? <CircularProgress size={24} /> : t('continue')}
          </Button>
        </Stack>
      </div>
    </div>
  )

  return isLarge ? getPopoverTimePicker(content) : getSwipeableTimePicker(content)
}

export default SwipeableTimePicker
